import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api';
import { deletePlayer, listPlayers } from '../api/players';
import { capitalizeWords } from '../utils/nameUtils';
import 'font-awesome/css/font-awesome.min.css';
import './Socias.css';

const hasValue = (value) => value !== null && value !== undefined && String(value).trim() !== '';
const getPlayerId = (player) => player?.id ?? player?.playerId ?? null;

const getDisplayName = (player) => {
    const first = player?.name ?? player?.firstName ?? '';
    const last = player?.lastName ?? '';
    const rawName = [first, last].filter(Boolean).join(' ').trim() || player?.fullName || '';
    return rawName ? capitalizeWords(String(rawName)) : 'Socia sin nombre';
};

const getInitials = (name) => {
    if (!name) return 'S';
    const parts = String(name).trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return 'S';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

const getLevelInfo = (player) => {
    if (!hasValue(player?.level)) return '-';
    const numeric = Number(player.level);
    return Number.isNaN(numeric) ? String(player.level) : numeric.toFixed(2);
};

/*const LEVEL_OPTIONS = Array.from({ length: 15 }, (_, index) => {
    const value = (index * 0.5).toFixed(1);
    return value.endsWith('.0') ? value.slice(0, -2) : value;
});*/

const Socias = () => {
    const navigate = useNavigate();
    const [players, setPlayers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({ name: '', lastName: '', level: '' });
    const [debouncedFilters, setDebouncedFilters] = useState(filters);

    const [editingLevelId, setEditingLevelId] = useState(null);
    const [levelDraft, setLevelDraft] = useState('');
    const [savingLevel, setSavingLevel] = useState(false);

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const [playerToDelete, setPlayerToDelete] = useState(null);
    const [deletingPlayerId, setDeletingPlayerId] = useState(null);

    useEffect(() => {
        const handler = setTimeout(() => setDebouncedFilters(filters), 400);
        return () => clearTimeout(handler);
    }, [filters]);

    useEffect(() => {
        let isCancelled = false;
        const fetchPlayers = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await listPlayers(debouncedFilters);
                if (!isCancelled) setPlayers(Array.isArray(data) ? data : []);
            } catch (err) {
                if (!isCancelled) setError('No se pudieron cargar las socias.');
            } finally {
                if (!isCancelled) setLoading(false);
            }
        };
        fetchPlayers();
        return () => { isCancelled = true; };
    }, [debouncedFilters]);

    const updatePlayerLevel = async (playerId, newLevel) => {
        if (!hasValue(playerId)) throw new Error('ID no encontrado');
        await apiClient.post(`/players/${playerId}/level`, { level: newLevel });
    };

    const handleFilterChange = (field) => (e) => {
        setFilters(prev => ({ ...prev, [field]: e.target.value }));
    };

    const handleStartEdit = (player) => {
        const id = getPlayerId(player);
        if (!id) return;
        setEditingLevelId(id);
        setLevelDraft(hasValue(player?.level) ? String(player.level) : '');
    };

    const handleCancelEdit = () => {
        setEditingLevelId(null);
        setLevelDraft('');
    };

    const handleSaveLevel = async (playerId) => {
        const normalized = levelDraft.replace(',', '.').trim();
        const numeric = Number(normalized);

        if (normalized === '' || Number.isNaN(numeric) || numeric < 0 || numeric > 7) {
            setSnackbar({ open: true, message: 'Nivel inválido (0-7)', severity: 'error' });
            return;
        }

        setSavingLevel(true);
        try {
            await updatePlayerLevel(playerId, numeric);
            setPlayers(prev => prev.map(p => getPlayerId(p) === playerId ? { ...p, level: numeric } : p));
            setSnackbar({ open: true, message: 'Nivel actualizado', severity: 'success' });
            handleCancelEdit();
        } catch (e) {
            setSnackbar({ open: true, message: 'Error al actualizar', severity: 'error' });
        } finally {
            setSavingLevel(false);
        }
    };

    const handleRequestDelete = (player) => setPlayerToDelete({ id: getPlayerId(player), name: getDisplayName(player) });

    const handleConfirmDelete = async () => {
        if (!playerToDelete?.id) return;
        setDeletingPlayerId(playerToDelete.id);
        try {
            await deletePlayer(playerToDelete.id);
            setPlayers(prev => prev.filter(p => getPlayerId(p) !== playerToDelete.id));
            setSnackbar({ open: true, message: 'Socia eliminada', severity: 'success' });
        } catch (e) {
            setSnackbar({ open: true, message: 'Error al eliminar', severity: 'error' });
        } finally {
            setDeletingPlayerId(null);
            setPlayerToDelete(null);
        }
    };

    const totalLabel = loading ? '...' : `${players.length} socia${players.length !== 1 ? 's' : ''}`;

    return (
        <div className="socias-page-wrapper">
            <div className="socias-container">

                

                <header className="socias-header">
                    <div className="socias-title">
                        <h1>Socias</h1>
                    </div>
                    <span className="total-badge">{totalLabel}</span>
                </header>

                <div className="filters-bar">
                    <div className="filter-group">
                        <label>Nombre</label>
                        <input
                            type="text"
                            className="filter-input"
                            placeholder="Buscar..."
                            value={filters.name}
                            onChange={handleFilterChange('name')}
                        />
                    </div>
                    <div className="filter-group">
                        <label>Apellido</label>
                        <input
                            type="text"
                            className="filter-input"
                            placeholder="Buscar..."
                            value={filters.lastName}
                            onChange={handleFilterChange('lastName')}
                        />
                    </div>
                    {/*<div className="filter-group">
                        <label>Nivel</label>
                        <select
                            className="filter-select"
                            value={filters.level}
                            onChange={handleFilterChange('level')}
                        >
                            <option value="">Todos</option>
                            {LEVEL_OPTIONS.map(opt => (
                                <option key={opt} value={opt}>Nivel {opt}</option>
                            ))}
                        </select>
                    </div>*/}
                </div>

                {loading ? (
                    <div className="loading-state">
                        <div className="spinner"></div>
                        <p>Cargando lista...</p>
                    </div>
                ) : error ? (
                    <div className="empty-state" style={{color:'#EF4444'}}>{error}</div>
                ) : players.length === 0 ? (
                    <div className="empty-state">No se encontraron socias.</div>
                ) : (
                    <div className="players-grid">
                        {players.map(player => {
                            const playerId = getPlayerId(player);
                            const displayName = getDisplayName(player);
                            const isEditing = editingLevelId === playerId;

                            return (
                                <div
                                    className="player-card"
                                    key={playerId || Math.random()}
                                    onClick={() => playerId && navigate(`/players/${playerId}`)}
                                    style={{ cursor: playerId ? 'pointer' : 'default' }}
                                >
                                    <div className="card-top">
                                        <div className="player-identity">
                                            <div className="avatar">{getInitials(displayName)}</div>
                                            <div className="player-info">
                                                <h3>{displayName}</h3>
                                                <span className="member-number">
                                                    {player.memberNumber ? `Socia #${player.memberNumber}` : 'Sin número'}
                                                </span>
                                            </div>
                                        </div>
                                        <button
                                            className="btn-delete-icon"
                                            onClick={(e) => { e.stopPropagation(); handleRequestDelete(player); }}
                                            title="Eliminar"
                                        >
                                            <i className="fa fa-trash-o"></i>
                                        </button>
                                    </div>

                                    <div className="info-section">
                                        <span className="section-label">Nivel</span>
                                        {isEditing ? (
                                            <div className="level-editor" onClick={(e) => e.stopPropagation()}>
                                                <input
                                                    className="level-input"
                                                    type="number" step="0.1"
                                                    value={levelDraft}
                                                    onChange={(e) => setLevelDraft(e.target.value)}
                                                    autoFocus
                                                />
                                                <button className="btn-mini save" onClick={() => handleSaveLevel(playerId)} disabled={savingLevel}>
                                                    <i className="fa fa-check"></i>
                                                </button>
                                                <button className="btn-mini cancel" onClick={handleCancelEdit} disabled={savingLevel}>
                                                    <i className="fa fa-times"></i>
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="level-display">
                                                <span className="level-value">{getLevelInfo(player)}</span>
                                                <button className="btn-edit-icon" onClick={(e) => { e.stopPropagation(); handleStartEdit(player); }}>
                                                    <i className="fa fa-pencil"></i>
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    <div className="info-section">
                                        <span className="section-label">Contacto</span>
                                        <div className="contact-list">
                                            {player.email ? (
                                                <div className="contact-item">
                                                    <i className="fa fa-envelope-o"></i> {player.email}
                                                </div>
                                            ) : (
                                                <div className="contact-item" style={{fontStyle:'italic', opacity:0.6}}>Sin email</div>
                                            )}
                                            {(player.phone || player.phoneNumber) && (
                                                <div className="contact-item">
                                                    <i className="fa fa-phone"></i> {player.phone || player.phoneNumber}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {playerToDelete && (
                <div className="modal-backdrop">
                    <div className="modal-content">
                        <h3>Eliminar Socia</h3>
                        <p>¿Seguro que quieres eliminar a <strong>{playerToDelete.name}</strong>? Esta acción no se puede deshacer.</p>
                        <div className="modal-actions">
                            <button className="btn-modal cancel" onClick={() => setPlayerToDelete(null)} disabled={deletingPlayerId}>
                                Cancelar
                            </button>
                            <button className="btn-modal danger" onClick={handleConfirmDelete} disabled={deletingPlayerId}>
                                {deletingPlayerId ? 'Eliminando...' : 'Eliminar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {snackbar.open && (
                <div className={`toast-notification ${snackbar.severity}`}>
                    {snackbar.message}
                </div>
            )}
        </div>
    );
};

export default Socias;